/**
 * Chinese (Simplified) catalog (Han) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `locale: 'zh-Hans'` (Simplified) so the script-subtag resolver selects it distinctly from
 * `zh-Hant`; `dir: 'ltr'`. Native-grade Simplified Chinese at a mathematician's register.
 * **Warrant → 「确证」** (epistemic justification/warrant; 保证/正当理由 are flagged
 * alternatives). Lakatos's "research programme" → 研究纲领; Popper's testable/tested →
 * 可检验/已检验. `scriptOf('zh-Hans')` → han, paired with the Han Noto-led font stack.
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose.
 * Chinese has NO grammatical plural: the only CLDR cardinal category is `other`, so
 * `frameActive` declares exactly `other` (plus an explicit `=0` refinement) and never invents
 * the one/few/many forms other locales need. Per INV-STABLE-ID a wrong translation is a
 * presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const ZH_HANS_CATALOG = {
    locale: 'zh-Hans',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: '探究',
        [P2.sectionClaims]: '主张',
        [P2.sectionIntegrity]: '完整性',
        [P2.axisWarrantLabel]: '确证',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: '经验',
        [P2.axisEmpiricalValue]: '{kind, select, defn {定义} interp {诠释} testable {可检验} tested {已检验} refuted {已反驳} program {研究纲领} philos {哲学} other {未知}}',
        [P2.axisModalLabel]: '模态',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: '强度等级 {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: '可探索',
        [P2.stateRefuted]: '已反驳',
        [P2.stateOutside]: '超出范围',
        [P2.verdictConflict]: '与 {constraints} 冲突',
        [P2.verdictOutside]: '需要 {missing}',
        [P2.lossRegisterPlain]: '已简化为通俗语言——略去部分精确性。',
        [P2.lossRegisterKid]: '已为年幼读者简化——略去许多细节。',
        [P2.lossProfileDetail]: '完整的确证／经验／模态概况在此汇总。',
        [P2.lossConflictDetail]: '冲突细节在此视图中已缩略。',
        [P2.laneVerified]: '已验证',
        [P2.laneUnverified]: '未验证',
        [P2.laneConflict]: '冲突已证实',
        [P2.constraintLogic]: '逻辑',
        [P2.constraintLightBends]: '光在引力场中弯曲',
        [P2.constraintParallelPostulate]: '平行公设',
        [P2.claimScalarGravity]: '标量引力理论',
        [P2.claimNonEuclidean]: '非欧几里得几何',
        [P2.claimSubstrateSr]: '狭义相对论的基底诠释',
        [P2.edgeExtObservation]: '外部观测',
        [P2.edgeResourceSample]: '共享计算资源',
        [P2.edgeHeuristicSample]: '启发式指引',
        [P2.edgeSharedSample]: '共同基础',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: '约束',
        [P2.sectionEdges]: '边',
        [P2.sectionFrame]: '框架',
        [P2.sectionExposure]: '暴露',
        [P2.statusAdoptedFormal]: '已采纳（形式）',
        [P2.statusAdoptedInterpretive]: '已采纳（诠释）',
        [P2.statusSuspended]: '已暂停',
        [P2.edgeEffectConstraint]: '构成约束（可反驳）',
        [P2.edgeEffectResource]: '资源——不影响裁定',
        [P2.edgeEffectHeuristic]: '启发式——不影响裁定',
        [P2.edgeEffectShared]: '共同基础——不影响裁定',
        [P2.frameActive]: '{count, plural, =0 {无活动约束} other {# 项活动约束}}',
        [P2.exposureNone]: '无诠释依赖',
        [P2.exposureDep]: '依赖于 {id}',
        [P2.laneTrackMachine]: '机器',
        [P2.laneTrackExpert]: '专家',
        [P2.laneTrackCommunity]: '社区',
        [P2.expertAudited]: '已审计',
        [P2.expertInProgress]: '进行中',
        [P2.expertNotReviewed]: '未审查',
        [P2.communityFlags]: '存在标记',
        [P2.communityNone]: '无标记',
        [P2.controlChallenge]: '质疑',
        [P2.controlActivate]: '激活',
        [P2.controlMakeInterpretive]: '设为诠释性',
        [P2.controlImportEdge]: '导入边',
        [P2.controlPromote]: '提升',
        [P2.controlUndo]: '撤销',
        [P2.controlRedo]: '重做',
        [P2.controlRegister]: '登记',
        [P2.promoteRejected]: '提升被拒',
        [P2.promoteAccepted]: '提升被接受',
        [P2.promoteReasonRefutationTerminal]: '证明无法消除反驳。',
        [P2.promoteReasonKeystone]: '关键性提升需要经验支持。',
        [P2.promoteReasonEmpiricalNotFixed]: '经验类型未固定。',
        [P2.promoteReasonModalNotFixed]: '模态等级未固定。',
        [P2.promoteReasonWarrantNotImproved]: '确证等级未提升。',
        [P2.evidenceComputation]: "{engine} 在 {config} 下计算出 {op} = {result}",
        [P2.lossNumericRounding]: "结果四舍五入到 {dp} 位小数",
    },
};
//# sourceMappingURL=zh-Hans.js.map